import { useCallback, useState } from "react";

import {
  Button,
  InfoBar,
  Link,
  Modal,
  type ModalProps,
  NumberInput,
  ServiceError,
  Spinner,
} from "@shapr3d/frontend-lib";
import { useQueryClient } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";

import { teamsArticleURL } from "../../../config";
import {
  TeamSubscriptionsEstimateQueryKey,
  useTeamsSubscriptionsEstimate,
  useUpdateTeamSubscription,
} from "../../../hooks/useTeams";
import type { SeatBasedTeamSubscription } from "../../../hooks/useTeamsTypes";
import { useTracker } from "../../../hooks/useTracker";
import { ElementClickAction, ElementClickLocation } from "../../../lib/TrackingAdapter";
import { ServiceErrorCode } from "../../../types";
import { inQuotes } from "../../../utils/convertUtils";
import { AddSeatsEstimateSummary } from "../../EstimateSummaries/AddSeatsEstimateSummary/AddSeatsEstimateSummary";
import { OverlayProvider } from "../../InfoTooltip/InfoTooltip";
import PaymentUpdateModal from "../../PaymentUpdateModal/PaymentUpdateModal";

import style from "./AddSeatsModal.module.css";

export const AddSeatsModal: React.FC<
  Pick<ModalProps, "open" | "onClose"> & {
    subscription: SeatBasedTeamSubscription;
    teamID: string | undefined;
  }
> = ({ subscription, teamID, onClose, open }) => {
  const [isPaymentUpdateModalOpen, setIsPaymentUpdateModalOpen] = useState(false);
  const tracker = useTracker();
  const queryClient = useQueryClient();

  const { watch, control, handleSubmit, reset } = useForm({
    defaultValues: { additionalEditorSeatCount: 1 },
  });

  const additionalEditorSeatCount = watch("additionalEditorSeatCount");

  const {
    data: estimateData,
    isLoading: isEstimateLoading,
    isFetching: isEstimateFetching,
    isPaused: isEstimatePaused,
    isPlaceholderData: isEstimatePreviousData,
    isError: isEstimateError,
  } = useTeamsSubscriptionsEstimate({
    subscription,
    additionalEditorSeatCount,
  });

  const {
    mutateAsync: mutateUpdateTeamSubscription,
    isPending: isUpdateTeamSubscriptionPending,
    isError: isUpdateTeamSubscriptionError,
    reset: resetUpdateTeamSubscription,
  } = useUpdateTeamSubscription({ teamID, subscriptionID: subscription.id });

  const resetState = useCallback(() => {
    reset();
    setIsPaymentUpdateModalOpen(false);
    resetUpdateTeamSubscription();
    queryClient.invalidateQueries({ queryKey: TeamSubscriptionsEstimateQueryKey });
    onClose?.();
  }, [reset, setIsPaymentUpdateModalOpen, onClose, resetUpdateTeamSubscription, queryClient]);

  const submit = useCallback(async () => {
    try {
      await mutateUpdateTeamSubscription({ additionalEditorSeatCount });
      resetState();
      toast.success(
        `${additionalEditorSeatCount} ${
          additionalEditorSeatCount > 1 ? `seats` : `seat`
        } added to your team.`
      );
    } catch (error) {
      if (
        error instanceof ServiceError &&
        error.hasCode(ServiceErrorCode.PaymentProcessingFailed)
      ) {
        setIsPaymentUpdateModalOpen(true);
        return;
      }
    }
  }, [mutateUpdateTeamSubscription, resetState, additionalEditorSeatCount]);

  return (
    <>
      <Modal
        className={style.modal}
        open={open && !isPaymentUpdateModalOpen}
        onClose={resetState}
        title={"Add seats"}
      >
        <OverlayProvider>
          <form className={style.wrapper} onSubmit={handleSubmit(submit)}>
            <Controller
              name="additionalEditorSeatCount"
              control={control}
              render={({ field: { onChange, value } }) => (
                <NumberInput
                  min={1}
                  max={50}
                  onChange={(_, value) => {
                    tracker.trackButtonClick({
                      action: ElementClickAction.SEAT_COUNT_CHANGE,
                      location: ElementClickLocation.ADD_SEATS_MODAL,
                      details: `${value ?? 1}`,
                    });
                    onChange(value ?? 1);
                  }}
                  value={value ?? 1}
                />
              )}
            />

            {estimateData && (
              <AddSeatsEstimateSummary
                isLoading={isEstimateFetching}
                currencySymbol={estimateData?.currencySymbol}
                proratedEstimate={estimateData?.prorated}
                renewalEstimate={estimateData?.renewal}
                renewalDate={estimateData?.renewalDate}
                additionalEditorSeatCount={additionalEditorSeatCount}
                subscription={subscription}
                paymentSource={estimateData?.primaryPaymentSource}
              />
            )}
            {isEstimateLoading && <Spinner size={40} />}
            {(isEstimateError || isUpdateTeamSubscriptionError) && (
              <InfoBar
                variant="warning"
                title="Adding new seats is unavailable at the moment."
                text="Please reload the page or try again later if the issue persists."
              />
            )}

            <div className={style.ctaRow}>
              <Link
                href={teamsArticleURL}
                target="_blank"
                referrerPolicy="no-referrer"
                onClick={() => {
                  tracker.trackButtonClick({
                    action: ElementClickAction.GO_TO_LEARN_MORE,
                    location: ElementClickLocation.ADD_SEATS_MODAL,
                  });
                }}
              >
                Learn more about teams.
              </Link>
              <Button
                variant="primary"
                type="submit"
                disabled={
                  isEstimateFetching ||
                  isEstimatePaused ||
                  isEstimatePreviousData ||
                  isUpdateTeamSubscriptionPending ||
                  isEstimateError
                }
                onClick={() => {
                  tracker.trackAddSeatsModalConfirmed({
                    seats: additionalEditorSeatCount,
                    trial: subscription.isTrial,
                  });
                }}
              >
                {subscription.isTrial ? "Add seats to trial" : "Buy seats"}
              </Button>
            </div>
          </form>
        </OverlayProvider>
      </Modal>
      <PaymentUpdateModal
        teamID={teamID}
        open={isPaymentUpdateModalOpen}
        onClose={resetState}
        initialError={
          <InfoBar
            variant="critical"
            title="Payment error, adding seats failed."
            text="Update your card details to add seats to your team."
          />
        }
        tertiaryText={
          estimateData?.prorated &&
          `Your card will get charged ${estimateData?.currencySymbol}${
            estimateData?.prorated.totalAmount
          } when you click ${inQuotes("Update & buy seats")}. ${
            estimateData.renewal.currencyCode === "USD"
              ? `All prices in ${estimateData.prorated.currencyCode}.`
              : ""
          }`
        }
        ctaText="Update & buy seats"
        onSuccess={async () => {
          await submit();
        }}
      />
    </>
  );
};
